import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrgContext from '../context/OrgContext';

const Inventory = () => {
    const { user, activeOrg } = useContext(OrgContext);
    const [locations, setLocations] = useState([]);
    const [articles, setArticles] = useState([]);
    const [filteredInventory, setFilteredInventory] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('all');
    const [selectedArticle, setSelectedArticle] = useState('all');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newInventory, setNewInventory] = useState({
        locationID: null,
        articleID: null,
        quantity: '',
        expiry: null,
    });

    useEffect(() => {
        if (activeOrg && user?.locationIDs) {
            const filteredLocations = user.locationIDs.filter(
                (location) => location.orgID === activeOrg._id
            );
            setLocations(filteredLocations);
        }
    }, [activeOrg, user]);

    useEffect(() => {
        if (activeOrg) {
            fetchArticles();
        }
    }, [activeOrg]);

    const fetchArticles = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/articles`, {
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                    orgID: activeOrg._id,
                },
            });
            setArticles(response.data || []);
        } catch (error) {
            console.error('Error fetching articles:', error);
            toast.error('Failed to load articles.');
        }
    };

    const fetchFilteredInventory = async () => {
        try {
            const params = {
                locationID: selectedLocation === 'all' ? undefined : selectedLocation,
                articleID: selectedArticle === 'all' ? undefined : selectedArticle,
            };

            const response = await axios.get(`${process.env.REACT_APP_SRV}/inventory`, {
                params,
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                    orgID: activeOrg._id,
                },
            });

            setFilteredInventory(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching inventory:', error);
            toast.error('Failed to load inventory.');
        }
    };

    const handleLocationChange = (selectedOption) => {
        setSelectedLocation(selectedOption?.value || 'all');
    };

    const handleArticleChange = (selectedOption) => {
        setSelectedArticle(selectedOption?.value || 'all');
    };

    const openDialog = () => {
        setNewInventory({
            locationID: null,
            articleID: null,
            quantity: '',
            expiry: null,
        });
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
    };

    const handleCreateInventory = async () => {
        try {
            const { locationID, articleID, quantity, expiry } = newInventory;
            if (!locationID || !articleID || !quantity) {
                toast.error('Please fill in all required fields.');
                return;
            }

            await axios.post(`${process.env.REACT_APP_SRV}/inventory`, {
                locationID,
                articleID,
                quantity: parseFloat(quantity),
                expiry: expiry || null,
            }, {
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                    orgID: activeOrg._id,
                },
            });

            toast.success('Inventory updated successfully.');
            fetchFilteredInventory(); // Refresh inventory data
            closeDialog();
        } catch (error) {
            console.error('Error creating inventory:', error);
            toast.error('Failed to update inventory.');
        }
    };

    const generateOptions = (data, labelKey = 'name') =>
        data.map((item) => ({
            value: item._id,
            label: item[labelKey],
        }));

    return (
        <div className="p-6 bg-gray-900 rounded-lg shadow-lg text-white">
            {/* Filters */}
            <div className="mb-4 flex flex-wrap gap-4">
                <div className="w-full md:w-1/2 lg:w-1/3">
                    <label className="block text-white mb-2">Location</label>
                    <Select
                        value={
                            selectedLocation === 'all'
                                ? { value: 'all', label: 'All Locations' }
                                : locations
                                    .map((loc) => ({ value: loc._id, label: loc.name }))
                                    .find((option) => option.value === selectedLocation) || null
                        }
                        onChange={handleLocationChange}
                        options={[
                            { value: 'all', label: 'All Locations' },
                            ...locations.map((loc) => ({
                                value: loc._id,
                                label: loc.name,
                            })),
                        ]}
                        placeholder="Search Location..."
                        theme={(theme) => ({
                            ...theme,
                            colors: {
                                ...theme.colors,
                                primary25: '#1F2937', // Darker hover background
                                primary: '#10B981', // Green accent
                                neutral0: '#111827', // Darker background
                                neutral80: '#F9FAFB', // Light text
                            },
                        })}
                    />

                </div>

                <div className="w-full md:w-1/2 lg:w-1/3">
                    <label className="block text-white mb-2">Article</label>
                    <Select
                        value={
                            selectedArticle === 'all'
                                ? { value: 'all', label: 'All Articles' }
                                : articles
                                    .map((art) => ({ value: art._id, label: art.name }))
                                    .find((option) => option.value === selectedArticle) || null
                        }
                        onChange={handleArticleChange}
                        options={[
                            { value: 'all', label: 'All Articles' },
                            ...articles.map((art) => ({
                                value: art._id,
                                label: art.name,
                            })),
                        ]}
                        placeholder="Search Article..."
                        theme={(theme) => ({
                            ...theme,
                            colors: {
                                ...theme.colors,
                                primary25: '#1F2937', // Darker hover background
                                primary: '#10B981', // Green accent
                                neutral0: '#111827', // Darker background
                                neutral80: '#F9FAFB', // Light text
                            },
                        })}
                    />

                </div>

                <div className="flex items-end">
                    <button
                        onClick={fetchFilteredInventory}
                        className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-700"
                    >
                        View
                    </button>
                </div>
                <div className="flex items-end">
                    <button
                        onClick={openDialog}
                        className="bg-green-600 px-4 py-2 rounded hover:bg-green-700"
                    >
                        Add Inventory
                    </button>
                </div>
            </div>

            {/* Inventory Table */}
            <div className="bg-gray-800 p-6 rounded-lg shadow">
                <table className="w-full table-auto text-white">
                    <thead>
                        <tr className="bg-gray-700">
                            <th className="px-4 py-2 text-left">Article</th>
                            <th className="px-4 py-2 text-left">Location</th>
                            <th className="px-4 py-2 text-right">Quantity</th>
                            <th className="px-4 py-2 text-right">Expiry</th>
                            <th className="px-4 py-2 text-right">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredInventory.length > 0 ? (
                            filteredInventory.map((item, index) => (
                                <tr key={`${item.articleName}-${item.locationName}-${item.expiry || index}`}>
                                    <td className="px-4 py-2 border-b text-left">{item.articleName}</td>
                                    <td className="px-4 py-2 border-b text-left">{item.locationName}</td>
                                    <td className="px-4 py-2 border-b text-right">{item.quantity}</td>
                                    <td className="px-4 py-2 border-b text-right">
                                        {item.expiry ? new Date(item.expiry).toLocaleDateString() : 'No Expiry'}
                                    </td>
                                    <td className="px-4 py-2 border-b text-right">
                                        {item.expiry && new Date(item.expiry) < new Date() ? (
                                            <span className="text-red-500">Expired</span>
                                        ) : (
                                            <span className="text-green-500">Fresh</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center py-4">No inventory data available.</td>
                            </tr>
                        )}
                    </tbody>
                </table>

            </div>

            {/* Add Inventory Dialog */}
            {isDialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-96">
                        <h3 className="text-xl text-white mb-4">Add Inventory</h3>
                        <div className="mb-4">
                            <label className="block text-white mb-2">Location</label>
                            <Select
                                value={
                                    locations
                                        .map((loc) => ({ value: loc._id, label: loc.name }))
                                        .find((option) => option.value === newInventory.locationID) || null
                                }
                                onChange={(option) =>
                                    setNewInventory({ ...newInventory, locationID: option.value })
                                }
                                options={locations.map((loc) => ({
                                    value: loc._id,
                                    label: loc.name,
                                }))}
                                placeholder="Search Location..."
                                theme={(theme) => ({
                                    ...theme,
                                    colors: {
                                        ...theme.colors,
                                        primary25: '#1F2937', // Darker hover background
                                        primary: '#10B981', // Green accent
                                        neutral0: '#111827', // Darker background
                                        neutral80: '#F9FAFB', // Light text
                                    },
                                })}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-white mb-2">Article</label>
                            <Select
                                value={
                                    articles
                                        .map((art) => ({ value: art._id, label: art.name }))
                                        .find((option) => option.value === newInventory.articleID) || null
                                }
                                onChange={(option) =>
                                    setNewInventory({ ...newInventory, articleID: option.value })
                                }
                                options={articles.map((art) => ({
                                    value: art._id,
                                    label: art.name,
                                }))}
                                placeholder="Search Article..."
                                theme={(theme) => ({
                                    ...theme,
                                    colors: {
                                        ...theme.colors,
                                        primary25: '#1F2937', // Darker hover background
                                        primary: '#10B981', // Green accent
                                        neutral0: '#111827', // Darker background
                                        neutral80: '#F9FAFB', // Light text
                                    },
                                })}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-white mb-2">Quantity</label>
                            <div className="flex items-center">
                                <input
                                    type="number"
                                    value={newInventory.quantity}
                                    onChange={(e) =>
                                        setNewInventory({ ...newInventory, quantity: e.target.value })
                                    }
                                    className="w-full px-4 py-2 bg-gray-700 text-white rounded"
                                    placeholder="Enter Quantity"
                                />
                                <span className="ml-2 text-white">
                                    {
                                        articles.find((art) => art._id === newInventory.articleID)?.unit || ''
                                    }
                                </span>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-white mb-2">Expiry (optional)</label>
                            <input
                                type="date"
                                value={newInventory.expiry || ''}
                                onChange={(e) =>
                                    setNewInventory({ ...newInventory, expiry: e.target.value })
                                }
                                className="w-full px-4 py-2 bg-gray-700 text-white rounded"
                            />
                        </div>
                        <div className="flex justify-between">
                            <button
                                onClick={handleCreateInventory}
                                className="bg-green-600 px-4 py-2 rounded text-white hover:bg-green-700"
                            >
                                Save
                            </button>
                            <button
                                onClick={closeDialog}
                                className="bg-red-600 px-4 py-2 rounded text-white hover:bg-red-700"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Inventory;
